"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Search, Tag } from "lucide-react";

const ROTATING_PLACEHOLDERS = [
  "What is LaunchLemonade?",
  "How much does LaunchLemonade cost?",
  "Is there a free plan?",
  "Do I need to know how to code?",
  "Is my client data safe on LaunchLemonade?",
  "Which AI models are available?",
  "What integrations does LaunchLemonade support?",
  "Is LaunchLemonade SOC 2 compliant?",
];

export type FaqItem = {
  q: string;
  a: string;
  tags: string[];
  sub?: string;
};

export type FaqGroup = {
  id: string;
  label: string;
  items: FaqItem[];
};

type PooledFaqItem = FaqItem & { groupId: string; groupLabel: string };

type FaqSection = {
  id: string;
  label: string | null;
  items: PooledFaqItem[];
};

export default function FaqExplorer({ groups }: { groups: FaqGroup[] }) {
  const [activeGroup, setActiveGroup] = useState("all");
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % ROTATING_PLACEHOLDERS.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const sections = useMemo(() => {
    const flat: PooledFaqItem[] = groups.flatMap((g) =>
      g.items.map((item) => ({ ...item, groupId: g.id, groupLabel: g.label })),
    );

    const seen = new Set<string>();
    const deduped = flat.filter((item) =>
      seen.has(item.q) ? false : (seen.add(item.q), true),
    );

    const q = query.trim().toLowerCase();
    const matched = q
      ? deduped.filter(
          (item) =>
            item.q.toLowerCase().includes(q) ||
            item.a.toLowerCase().includes(q) ||
            item.tags.some((tag) => tag.toLowerCase().includes(q)),
        )
      : deduped;

    if (activeGroup !== "all") {
      const group = groups.find((g) => g.id === activeGroup);
      const groupItems = matched.filter((item) => item.groupId === activeGroup);

      const hasSubLabels = groupItems.some((item) => item.sub);
      if (hasSubLabels) {
        const order: string[] = [];
        const bySub = new Map<string, PooledFaqItem[]>();
        for (const item of groupItems) {
          const key = item.sub ?? group?.label ?? "";
          if (!bySub.has(key)) {
            bySub.set(key, []);
            order.push(key);
          }
          bySub.get(key)!.push(item);
        }
        return order.map((label) => ({
          id: `${activeGroup}-${label}`,
          label,
          items: bySub.get(label)!,
        })) satisfies FaqSection[];
      }

      return [
        {
          id: activeGroup,
          label: group?.label ?? null,
          items: groupItems,
        },
      ] satisfies FaqSection[];
    }

    return groups
      .map((g) => ({
        id: g.id,
        label: g.label,
        items: matched.filter((item) => item.groupId === g.id),
      }))
      .filter((section) => section.items.length > 0) satisfies FaqSection[];
  }, [groups, activeGroup, query]);

  const totalCount = sections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div>
      <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h2
            className="font-heading mb-4 text-2xl font-bold text-on-dark"
            style={{
              background: "none",
              WebkitBackgroundClip: "unset",
              backgroundClip: "unset",
            }}
          >
            Categories:
          </h2>
          <div className="inline-flex max-w-full flex-wrap gap-1 rounded-[28px] border border-hairline bg-panel-solid/70 p-1.5">
            {[{ id: "all", label: "All" }, ...groups].map(({ id, label }) => {
              const active = id === activeGroup;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setActiveGroup(id)}
                  className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                    active
                      ? "bg-secondary font-medium text-secondary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <form
          role="search"
          onSubmit={(e) => e.preventDefault()}
          className="relative w-full shrink-0 lg:w-[360px] xl:w-[430px]"
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search by questions and tags..."
            aria-label="Search by questions and tags"
            className="faq-search h-[52px] w-full rounded-full border bg-panel-solid pr-14 pl-6 text-[15px] text-foreground outline-none placeholder:text-muted-foreground/70 [&::-webkit-search-cancel-button]:hidden"
          />
          {!searchFocused && query.length === 0 && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-[1px] flex items-center overflow-hidden rounded-full bg-panel-solid pr-14 pl-6"
            >
              <span
                key={placeholderIndex}
                className="faq-rotating-placeholder truncate text-[15px] text-muted-foreground/70"
              >
                {ROTATING_PLACEHOLDERS[placeholderIndex]}
              </span>
            </div>
          )}
          <button
            type="submit"
            aria-label="Search"
            className="absolute top-1/2 right-1.5 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#f5c518] text-[#0a0e1a] transition-colors hover:bg-[#ffd93d]"
          >
            <Search className="h-4.5 w-4.5" strokeWidth={2.5} />
          </button>
        </form>
      </div>

      {totalCount > 0 ? (
        <div className="flex flex-col gap-8">
          {sections.map((section) => (
            <div key={section.id}>
              {section.label && (
                <div className="mb-3 flex items-center gap-3">
                  <h3 className="font-heading shrink-0 text-sm font-semibold tracking-wide text-primary uppercase">
                    {section.label}
                  </h3>
                  <span className="h-px flex-1 bg-hairline" aria-hidden="true" />
                </div>
              )}
              <div className="flex flex-col gap-3">
                {section.items.map((item) => (
                  <details
                    key={item.q}
                    name="faq"
                    className="faq-item faq-row group rounded-2xl border bg-card px-4 py-3.5 md:px-5"
                  >
                    <summary className="flex min-h-[44px] cursor-pointer items-center gap-4 text-left">
                      <span
                        aria-hidden="true"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-inset-border bg-inset text-muted-foreground transition-colors group-open:text-foreground"
                      >
                        <ChevronRight className="h-4 w-4 transition-transform duration-300 group-open:rotate-90" />
                      </span>
                      <span className="font-heading flex-1 text-[17px] font-medium text-foreground md:text-lg">
                        {item.q}
                      </span>
                      <span className="hidden shrink-0 flex-wrap justify-end gap-2 md:flex">
                        {item.tags.map((tag) => (
                          <span
                            key={tag}
                            className="flex items-center gap-1.5 rounded-full border border-inset-border bg-inset px-3.5 py-1.5 text-[13px] text-muted-foreground"
                          >
                            <Tag className="h-3.5 w-3.5 opacity-60" />
                            {tag}
                          </span>
                        ))}
                      </span>
                    </summary>
                    <p className="pt-3 pr-2 pb-1.5 text-[15px] leading-relaxed text-muted-foreground md:pl-[52px]">
                      {item.a}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-hairline-strong border-dashed bg-card/50 px-6 py-14 text-center">
          <p className="text-muted-foreground">
            No questions match your search. Try a different term or category.
          </p>
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setActiveGroup("all");
            }}
            className="mt-4 rounded-full border px-5 py-2 text-sm text-[#4fd1c5] transition-colors hover:bg-[#4fd1c5]/10"
            style={{ borderColor: "rgba(79,209,197,0.4)" }}
          >
            Clear search
          </button>
        </div>
      )}
    </div>
  );
}
